import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  GraduationCap, Globe, MapPin, Laptop, CheckCircle, Upload,
  Send, Calendar, Users, Target, Briefcase, FileText, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const internshipDepartments = [
  'Youth Empowerment',
  'Gender Equality',
  'Climate Action',
  'Research & Policy',
  'Digital Communications',
  'Project Management',
  'Monitoring & Evaluation'
];

const relevantSkills = [
  'Research',
  'Communication',
  'Data Analysis',
  'Social Media Management',
  'Community Engagement',
  'Project Coordination'
];

const preferredQualifications = [
  'Academic background in social sciences, environmental studies, international relations, development studies, IT, digital communications, or related fields',
  'Experience in website content management (WordPress, Elementor)',
  'Knowledge of social media content creation and digital storytelling',
  'Basic skills in graphic design tools (Canva, Adobe Express)',
  'Ability to support digital engagement through social media management',
  'Familiarity with SEO principles, newsletter tools, or email marketing platforms',
  'Experience in volunteering, advocacy, or community development',
  'Strong written and verbal communication skills in English'
];

const sdgOptions = [
  'SDG 1: No Poverty',
  'SDG 2: Zero Hunger',
  'SDG 3: Good Health and Well-being',
  'SDG 4: Quality Education',
  'SDG 5: Gender Equality',
  'SDG 6: Clean Water and Sanitation',
  'SDG 7: Affordable and Clean Energy',
  'SDG 8: Decent Work and Economic Growth',
  'SDG 9: Industry, Innovation and Infrastructure',
  'SDG 10: Reduced Inequalities',
  'SDG 11: Sustainable Cities and Communities',
  'SDG 12: Responsible Consumption and Production',
  'SDG 13: Climate Action',
  'SDG 14: Life Below Water',
  'SDG 15: Life on Land',
  'SDG 16: Peace, Justice and Strong Institutions',
  'SDG 17: Partnerships for the Goals'
];

export default function InternshipPage() {
  const [formData, setFormData] = useState({
    // Section A: Personal Information
    full_name: '',
    gender: '',
    date_of_birth: '',
    nationality: '',
    country_of_residence: '',
    email: '',
    phone: '',
    linkedin: '',
    // Section B: Educational Background
    academic_status: '',
    institution_name: '',
    field_of_study: '',
    expected_graduation: '',
    // Section C: Internship Interest
    preferred_department: '',
    motivation: '',
    sdg_passion: '',
    skills_to_gain: '',
    // Section D: Experience
    has_experience: '',
    experience_description: '',
    relevant_skills: [],
    // Section E: Availability
    full_commitment: '',
    has_computer_internet: '',
    willing_to_submit_reports: '',
    // Section F: Supporting Information
    cv_file: null,
    motivation_letter: null,
    heard_about_opad: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cvFileName, setCvFileName] = useState('');
  const [motivationFileName, setMotivationFileName] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSkillToggle = (skill) => {
    setFormData(prev => ({
      ...prev,
      relevant_skills: prev.relevant_skills.includes(skill)
        ? prev.relevant_skills.filter(s => s !== skill)
        : [...prev.relevant_skills, skill]
    }));
  };

  const handleCvChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, cv_file: file }));
      setCvFileName(file.name);
    }
  };

  const handleMotivationLetterChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, motivation_letter: file }));
      setMotivationFileName(file.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.preferred_department) {
      toast.error('Please select a preferred internship department');
      return;
    }
    
    if (!formData.cv_file) {
      toast.error('Please upload your CV/Resume');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const submitData = new FormData();
      
      Object.keys(formData).forEach(key => {
        if (key === 'cv_file' && formData[key]) {
          submitData.append('cv_file', formData[key]);
        } else if (key === 'motivation_letter' && formData[key]) {
          submitData.append('motivation_letter', formData[key]);
        } else if (Array.isArray(formData[key])) {
          submitData.append(key, JSON.stringify(formData[key]));
        } else if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });
      
      await axios.post(`${API}/internships`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Your internship application has been submitted successfully! We will review and contact you soon.');
      
      // Reset form
      setFormData({
        full_name: '',
        gender: '',
        date_of_birth: '',
        nationality: '',
        country_of_residence: '',
        email: '',
        phone: '',
        linkedin: '',
        academic_status: '',
        institution_name: '',
        field_of_study: '',
        expected_graduation: '',
        preferred_department: '',
        motivation: '',
        sdg_passion: '',
        skills_to_gain: '',
        has_experience: '',
        experience_description: '',
        relevant_skills: [],
        full_commitment: '',
        has_computer_internet: '',
        willing_to_submit_reports: '',
        cv_file: null,
        motivation_letter: null,
        heard_about_opad: ''
      });
      setCvFileName('');
      setMotivationFileName('');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="internship-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E08B7A] via-[#E99A8A] to-[#F0A99A]">
          <div className="absolute top-0 right-0 w-[500px] h-[450px] bg-white/20 rounded-bl-[250px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[350px] bg-[#7BC4E2]/30 rounded-tr-[180px]" />
          <div className="absolute top-[30%] left-[10%] w-[150px] h-[150px] bg-[#E5B75F]/20 rounded-full" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <GraduationCap className="w-16 h-16 text-white mx-auto mb-6" />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] text-white mb-6">
                OPAD Internship Opportunities
              </h1>
              <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
                Gain practical, impact-driven experience in global humanitarian and development work 
                aligned with the United Nations Sustainable Development Goals (SDGs).
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* OPAD Team Banner Image */}
      <section className="relative overflow-hidden" data-testid="intern-team-banner">
        <motion.div
          className="relative"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_10eec864-0b4d-4948-b760-389570b9159b/artifacts/ij1sathy_ChatGPT%20Image%20Feb%205%2C%202026%2C%2004_35_43%20AM.png"
            alt="OPAD Team - Together, we can make a difference. Empowering • Transforming • Building a Better Future"
            className="w-full h-auto object-cover"
            data-testid="intern-team-image"
          />
        </motion.div>
      </section>

      {/* Internship Types */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[450px] h-[400px] bg-[#7BC4E2]/10 rounded-br-[200px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[350px] bg-[#E08B7A]/10 rounded-tl-[180px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* In-Person Internship */}
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-slate-100"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              data-testid="in-person-internship-card"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-[#4A7C59]/20 rounded-xl flex items-center justify-center">
                  <MapPin className="w-7 h-7 text-[#4A7C59]" />
                </div>
                <h3 className="text-xl font-bold font-['Manrope'] text-slate-900">
                  In-Person Internship Programme
                </h3>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                OPAD's In-Person Internship Programme is designed for individuals who wish to gain 
                hands-on field experience by directly participating in community-based projects and 
                development initiatives. Interns are engaged in programme implementation, volunteer 
                coordination, research activities, and stakeholder engagement within OPAD's operational 
                environments.
              </p>
              <p className="text-slate-600 text-sm leading-relaxed">
                This internship provides valuable exposure to real-world humanitarian operations, 
                teamwork in multicultural settings, and direct contribution to social impact initiatives 
                aligned with the United Nations Sustainable Development Goals (SDGs).
              </p>
              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Opportunities will be announced in future programme cycles.
                </p>
              </div>
            </motion.div>

            {/* Remote Internship */}
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border-2 border-[#7BC4E2] ring-2 ring-[#7BC4E2]/20"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              data-testid="remote-internship-card"
            >
              <div className="absolute -top-3 left-6">
                <span className="bg-[#7BC4E2] text-white text-xs font-semibold px-3 py-1 rounded-full">
                  NOW RECRUITING
                </span>
              </div>
              <div className="flex items-center gap-4 mb-4 mt-2">
                <div className="w-14 h-14 bg-[#7BC4E2]/20 rounded-xl flex items-center justify-center">
                  <Laptop className="w-7 h-7 text-[#7BC4E2]" />
                </div>
                <h3 className="text-xl font-bold font-['Manrope'] text-slate-900">
                  Remote Internship Programme
                </h3>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                OPAD's Remote Internship Programme offers a flexible and globally accessible opportunity 
                for students and young professionals to contribute to international development work from 
                anywhere in the world. Remote interns participate in research, programme development, 
                digital advocacy, policy analysis, and project support activities.
              </p>
              <p className="text-slate-600 text-sm leading-relaxed">
                This programme enables interns to develop professional skills in global project coordination, 
                reporting, and SDG-focused initiatives within an international virtual work environment.
              </p>
              <div className="mt-4 p-3 bg-[#7BC4E2]/10 rounded-lg border border-[#7BC4E2]/30">
                <p className="text-sm text-[#0F172A] flex items-center gap-2 font-medium">
                  <Calendar className="w-4 h-4 text-[#7BC4E2]" />
                  February – April 2026 Cohort Open
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Preferred Qualifications */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-[#FDF8F4]">
          <div className="absolute top-0 right-0 w-[500px] h-[450px] bg-[#4A7C59]/10 rounded-bl-[250px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[350px] bg-[#E5B75F]/10 rounded-tr-[180px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-slate-100 mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-6">
              <Target className="w-6 h-6 text-[#E08B7A]" />
              <h3 className="text-xl font-bold font-['Manrope'] text-slate-900">
                Preferred Qualifications
              </h3>
            </div>
            <p className="text-slate-600 mb-4">
              Applicants with the following qualifications will be given priority consideration:
            </p>
            <ul className="grid md:grid-cols-2 gap-3">
              {preferredQualifications.map((qual, index) => (
                <li key={index} className="flex items-start gap-2 text-slate-600 text-sm">
                  <CheckCircle className="w-5 h-5 text-[#4A7C59] flex-shrink-0 mt-0.5" />
                  <span>{qual}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 p-4 bg-[#7BC4E2]/10 rounded-lg border border-[#7BC4E2]/30">
              <p className="text-sm text-slate-700">
                <strong>Note:</strong> Interns with experience in website updates, digital campaign support, 
                or social media management may be assigned to assist with OPAD's online outreach activities 
                for community programmes in Sweden and global events.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Application Form */}
      <section className="relative section-padding overflow-hidden" id="apply" data-testid="internship-form-section">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[450px] h-[400px] bg-[#E08B7A]/10 rounded-br-[200px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[350px] bg-[#7BC4E2]/10 rounded-tl-[180px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">
                Internship Application Form
              </h2>
              <p className="text-slate-600">
                Apply for OPAD Remote Internship Programme (February – April 2026)
              </p>
            </div>

            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-slate-100"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <form onSubmit={handleSubmit} data-testid="internship-form">
                {/* Section A: Personal Information */}
                <div className="mb-10">
                  <h4 className="text-sm font-semibold text-[#E08B7A] uppercase tracking-wider mb-6 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Section A: Personal Information
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="full_name">Full Name *</Label>
                      <Input
                        id="full_name"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="intern-full-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="gender">Gender *</Label>
                      <Select 
                        value={formData.gender}
                        onValueChange={(value) => handleSelectChange('gender', value)}
                      >
                        <SelectTrigger className="mt-1" data-testid="intern-gender">
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="non-binary">Non-binary</SelectItem>
                          <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="date_of_birth">Date of Birth *</Label>
                      <Input
                        id="date_of_birth"
                        name="date_of_birth"
                        type="date"
                        value={formData.date_of_birth}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="intern-dob"
                      />
                    </div>
                    <div>
                      <Label htmlFor="nationality">Nationality *</Label>
                      <Input
                        id="nationality"
                        name="nationality"
                        value={formData.nationality}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="intern-nationality"
                      />
                    </div>
                    <div>
                      <Label htmlFor="country_of_residence">Country of Residence *</Label>
                      <Input
                        id="country_of_residence"
                        name="country_of_residence"
                        value={formData.country_of_residence}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="intern-country"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="intern-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number (Including Country Code) *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="+1 234 567 8900"
                        className="mt-1"
                        data-testid="intern-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="linkedin">LinkedIn Profile (Optional)</Label>
                      <Input
                        id="linkedin"
                        name="linkedin"
                        value={formData.linkedin}
                        onChange={handleChange}
                        placeholder="https://linkedin.com/in/..."
                        className="mt-1"
                        data-testid="intern-linkedin"
                      />
                    </div>
                  </div>
                </div>

                {/* Section B: Educational Background */}
                <div className="mb-10">
                  <h4 className="text-sm font-semibold text-[#E08B7A] uppercase tracking-wider mb-6 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" />
                    Section B: Educational Background
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="academic_status">Current Academic Status *</Label>
                      <Select 
                        value={formData.academic_status}
                        onValueChange={(value) => handleSelectChange('academic_status', value)}
                      >
                        <SelectTrigger className="mt-1" data-testid="intern-academic-status">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="undergraduate">Undergraduate Student</SelectItem>
                          <SelectItem value="postgraduate">Postgraduate Student</SelectItem>
                          <SelectItem value="recent-graduate">Recent Graduate</SelectItem>
                          <SelectItem value="young-professional">Young Professional</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="institution_name">Name of University / Institution *</Label>
                      <Input
                        id="institution_name"
                        name="institution_name"
                        value={formData.institution_name}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="intern-institution"
                      />
                    </div>
                    <div>
                      <Label htmlFor="field_of_study">Field of Study *</Label>
                      <Input
                        id="field_of_study"
                        name="field_of_study"
                        value={formData.field_of_study}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="intern-field"
                      />
                    </div>
                    <div>
                      <Label htmlFor="expected_graduation">Expected Graduation Date</Label>
                      <Input
                        id="expected_graduation"
                        name="expected_graduation"
                        type="month"
                        value={formData.expected_graduation}
                        onChange={handleChange}
                        className="mt-1"
                        data-testid="intern-graduation"
                      />
                    </div>
                  </div>
                </div>

                {/* Section C: Internship Interest */}
                <div className="mb-10">
                  <h4 className="text-sm font-semibold text-[#E08B7A] uppercase tracking-wider mb-6 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Section C: Internship Interest
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="preferred_department">Preferred Internship Department *</Label>
                      <Select 
                        value={formData.preferred_department}
                        onValueChange={(value) => handleSelectChange('preferred_department', value)}
                      >
                        <SelectTrigger className="mt-1" data-testid="intern-department">
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent>
                          {internshipDepartments.map((dept) => (
                            <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="motivation">Why are you interested in joining OPAD's Remote Internship Programme? *</Label>
                      <Textarea
                        id="motivation"
                        name="motivation"
                        value={formData.motivation}
                        onChange={handleChange}
                        required
                        rows={3}
                        className="mt-1"
                        data-testid="intern-motivation"
                      />
                    </div>
                    <div>
                      <Label htmlFor="sdg_passion">Which Sustainable Development Goal (SDG) are you most passionate about and why? *</Label>
                      <div className="mt-1 mb-2">
                        <Select 
                          value={formData.sdg_passion.split(':')[0] ? formData.sdg_passion : ''}
                          onValueChange={(value) => handleSelectChange('sdg_passion', value)}
                        >
                          <SelectTrigger data-testid="intern-sdg">
                            <SelectValue placeholder="Select an SDG" />
                          </SelectTrigger>
                          <SelectContent>
                            {sdgOptions.map((sdg) => (
                              <SelectItem key={sdg} value={sdg}>{sdg}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="skills_to_gain">What skills or experience do you hope to gain from this internship? *</Label>
                      <Textarea
                        id="skills_to_gain"
                        name="skills_to_gain"
                        value={formData.skills_to_gain}
                        onChange={handleChange}
                        required
                        rows={3}
                        className="mt-1"
                        data-testid="intern-skills-gain"
                      />
                    </div>
                  </div>
                </div>

                {/* Section D: Experience */}
                <div className="mb-10">
                  <h4 className="text-sm font-semibold text-[#E08B7A] uppercase tracking-wider mb-6 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Section D: Experience
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <Label>Do you have previous volunteering or internship experience? *</Label>
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="has_experience"
                            value="yes"
                            checked={formData.has_experience === 'yes'}
                            onChange={handleChange}
                            className="w-4 h-4 text-[#7BC4E2]"
                            data-testid="intern-exp-yes"
                          />
                          <span className="text-slate-700">Yes</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="has_experience"
                            value="no"
                            checked={formData.has_experience === 'no'}
                            onChange={handleChange}
                            className="w-4 h-4 text-[#7BC4E2]"
                            data-testid="intern-exp-no"
                          />
                          <span className="text-slate-700">No</span>
                        </label>
                      </div>
                    </div>
                    {formData.has_experience === 'yes' && (
                      <div>
                        <Label htmlFor="experience_description">Please briefly describe your experience</Label>
                        <Textarea
                          id="experience_description"
                          name="experience_description"
                          value={formData.experience_description}
                          onChange={handleChange}
                          rows={3}
                          className="mt-1"
                          data-testid="intern-exp-desc"
                        />
                      </div>
                    )}
                    <div>
                      <Label>What relevant skills do you possess? *</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                        {relevantSkills.map((skill) => (
                          <div key={skill} className="flex items-center space-x-2">
                            <Checkbox
                              id={`skill-${skill}`}
                              checked={formData.relevant_skills.includes(skill)}
                              onCheckedChange={() => handleSkillToggle(skill)}
                              data-testid={`skill-${skill.toLowerCase().replace(/\s+/g, '-')}`}
                            />
                            <label 
                              htmlFor={`skill-${skill}`}
                              className="text-sm text-slate-600 cursor-pointer"
                            >
                              {skill}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section E: Availability & Commitment */}
                <div className="mb-10">
                  <h4 className="text-sm font-semibold text-[#E08B7A] uppercase tracking-wider mb-6 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Section E: Availability & Commitment
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <Label>Are you available to commit for the full internship duration? *</Label>
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="full_commitment"
                            value="yes"
                            checked={formData.full_commitment === 'yes'}
                            onChange={handleChange}
                            required
                            className="w-4 h-4 text-[#7BC4E2]"
                            data-testid="intern-commit-yes"
                          />
                          <span className="text-slate-700">Yes</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="full_commitment"
                            value="no"
                            checked={formData.full_commitment === 'no'}
                            onChange={handleChange}
                            className="w-4 h-4 text-[#7BC4E2]"
                            data-testid="intern-commit-no"
                          />
                          <span className="text-slate-700">No</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <Label>Do you have access to a computer and stable internet connection? *</Label>
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="has_computer_internet"
                            value="yes"
                            checked={formData.has_computer_internet === 'yes'}
                            onChange={handleChange}
                            required
                            className="w-4 h-4 text-[#7BC4E2]"
                            data-testid="intern-computer-yes"
                          />
                          <span className="text-slate-700">Yes</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="has_computer_internet"
                            value="no"
                            checked={formData.has_computer_internet === 'no'}
                            onChange={handleChange}
                            className="w-4 h-4 text-[#7BC4E2]"
                            data-testid="intern-computer-no"
                          />
                          <span className="text-slate-700">No</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <Label>Are you willing to submit monthly reports and a final testimonial video? *</Label>
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="willing_to_submit_reports"
                            value="yes"
                            checked={formData.willing_to_submit_reports === 'yes'}
                            onChange={handleChange}
                            required
                            className="w-4 h-4 text-[#7BC4E2]"
                            data-testid="intern-reports-yes"
                          />
                          <span className="text-slate-700">Yes</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="willing_to_submit_reports"
                            value="no"
                            checked={formData.willing_to_submit_reports === 'no'}
                            onChange={handleChange}
                            className="w-4 h-4 text-[#7BC4E2]"
                            data-testid="intern-reports-no"
                          />
                          <span className="text-slate-700">No</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section F: Supporting Information */}
                <div className="mb-10">
                  <h4 className="text-sm font-semibold text-[#E08B7A] uppercase tracking-wider mb-6 flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    Section F: Supporting Information
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <Label>Upload CV / Resume *</Label>
                      <div className="mt-2">
                        <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-[#E08B7A] transition-colors">
                          <div className="text-center">
                            <Upload className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                            <span className="text-sm text-slate-600">
                              {cvFileName || 'Click to upload (PDF, DOC, DOCX)'}
                            </span>
                          </div>
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleCvChange}
                            className="hidden"
                            data-testid="intern-cv-upload"
                          />
                        </label>
                      </div>
                    </div>
                    <div>
                      <Label>Upload Motivation Letter (Optional)</Label>
                      <div className="mt-2">
                        <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-[#E08B7A] transition-colors">
                          <div className="text-center">
                            <Upload className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                            <span className="text-sm text-slate-600">
                              {motivationFileName || 'Click to upload (PDF, DOC, DOCX)'}
                            </span>
                          </div>
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleMotivationLetterChange}
                            className="hidden"
                            data-testid="intern-motivation-upload"
                          />
                        </label>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="heard_about_opad">How did you hear about OPAD?</Label>
                      <Select 
                        value={formData.heard_about_opad}
                        onValueChange={(value) => handleSelectChange('heard_about_opad', value)}
                      >
                        <SelectTrigger className="mt-1" data-testid="intern-heard-about">
                          <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="social-media">Social Media</SelectItem>
                          <SelectItem value="website">OPAD Website</SelectItem>
                          <SelectItem value="friend-referral">Friend/Referral</SelectItem>
                          <SelectItem value="university">University/Institution</SelectItem>
                          <SelectItem value="job-board">Job Board</SelectItem>
                          <SelectItem value="search-engine">Search Engine</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Declaration */}
                <div className="mb-6">
                  <div className="flex items-start space-x-3">
                    <Checkbox id="intern-declaration" required data-testid="intern-declaration" />
                    <label htmlFor="intern-declaration" className="text-sm text-slate-600">
                      I confirm that the information provided is accurate and that I agree to comply 
                      with OPAD's policies and internship guidelines.
                    </label>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#E08B7A] hover:bg-[#D07A69] text-white rounded-full py-6 font-semibold text-lg"
                  disabled={isSubmitting}
                  data-testid="intern-submit-button"
                >
                  {isSubmitting ? (
                    <>Submitting...</>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Apply for OPAD Remote Internship Programme
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4A7C59] via-[#5A8C69] to-[#6A9C79]">
          <div className="absolute top-0 right-0 w-[400px] h-[350px] bg-white/10 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-[#E5B75F]/20 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Globe className="w-16 h-16 text-white mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white mb-4">
              Shape Your Future with OPAD
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Join a global community of young leaders making a difference in humanitarian 
              and development work.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
